import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AppProvider, useApp } from "@/contexts/AppContext";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import AcceptInvite from "@/pages/AcceptInvite";
import RegisterHospital from "@/pages/RegisterHospital";
import PitchDeck from "@/pages/PitchDeck";
import GrantDocument from "@/pages/GrantDocument";
import PressKit from "@/pages/PressKit";
import DashboardLayout from "@/pages/DashboardLayout";
import DashboardHome from "@/pages/dashboard/Home";
import Patients from "@/pages/dashboard/Patients";
import PatientPrint from "@/pages/dashboard/PatientPrint";
import Hospitalizations from "@/pages/dashboard/Hospitalizations";
import Deaths from "@/pages/dashboard/Deaths";
import Appointments from "@/pages/dashboard/Appointments";
import Campaigns from "@/pages/dashboard/Campaigns";
import Advice from "@/pages/dashboard/Advice";
import SMSLog from "@/pages/dashboard/SMSLog";
import Team from "@/pages/dashboard/Team";
import Audit from "@/pages/dashboard/Audit";
import Subscribe from "@/pages/dashboard/Subscribe";
import MyDay from "@/pages/dashboard/MyDay";
import SuperAdminLayout from "@/pages/SuperAdminLayout";
import SuperHome from "@/pages/superadmin/Home";
import SuperHospitals from "@/pages/superadmin/Hospitals";
import SuperHospitalDetail from "@/pages/superadmin/HospitalDetail";
import SuperAudit from "@/pages/superadmin/Audit";
import SuperMessages from "@/pages/superadmin/Messages";
import SuperStaff from "@/pages/superadmin/Staff";
import SuperFinance from "@/pages/superadmin/Finance";
import SuperMinistry from "@/pages/superadmin/Ministry";
import SuperSubscriptions from "@/pages/superadmin/Subscriptions";
import MinistryDashboard from "@/pages/MinistryDashboard";
import MinistryReport from "@/pages/MinistryReport";
import { ProvinceDetail, HospitalStats } from "@/pages/MinistryExplore";
import Urgences from "@/pages/dashboard/services/Urgences";
import Maternite from "@/pages/dashboard/services/Maternite";
import Pediatrie from "@/pages/dashboard/services/Pediatrie";
import Pharmacie from "@/pages/dashboard/services/Pharmacie";
import EpidemicAlerts from "@/pages/dashboard/EpidemicAlerts";
import Dhis2Export from "@/pages/dashboard/Dhis2Export";
import TrainingGuide from "@/pages/TrainingGuide";
import FormationQuiz, { FormationCertificate } from "@/pages/FormationQuiz";
import Contact from "@/pages/Contact";
import GoogleAnalytics from "@/components/GoogleAnalytics";

function ProtectedRoute({ children, allow }) {
  const { token, user } = useApp();
  if (!token) return <Navigate to="/login" replace />;
  if (allow && !allow.includes(user?.role)) return <Navigate to="/login" replace />;
  return children;
}

function HomeRedirect() {
  const { token, user } = useApp();
  if (!token) return <Landing />;
  if (user?.role === "super_admin") return <Navigate to="/super-admin" replace />;
  if (user?.role === "ministry") return <Navigate to="/ministry" replace />;
  return <Navigate to="/dashboard" replace />;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/accept-invite" element={<AcceptInvite />} />
      <Route path="/register-hospital" element={<RegisterHospital />} />
      <Route path="/pitch" element={<PitchDeck />} />
      <Route path="/dossier-subventions" element={<GrantDocument />} />
      <Route path="/presse" element={<PressKit />} />
      <Route path="/investors" element={<PressKit />} />
      <Route path="/contact" element={<Contact />} />

      <Route path="/dashboard/patients/:id/print" element={
        <ProtectedRoute allow={["hospital_admin", "doctor", "nurse", "caregiver", "secretary"]}>
          <PatientPrint />
        </ProtectedRoute>
      } />

      <Route path="/super-admin" element={
        <ProtectedRoute allow={["super_admin"]}>
          <SuperAdminLayout />
        </ProtectedRoute>
      }>
        <Route index element={<SuperHome />} />
        <Route path="hospitals" element={<SuperHospitals />} />
        <Route path="hospitals/:id" element={<SuperHospitalDetail />} />
        <Route path="messages" element={<SuperMessages />} />
        <Route path="staff" element={<SuperStaff />} />
        <Route path="finance" element={<SuperFinance />} />
        <Route path="subscriptions" element={<SuperSubscriptions />} />
        <Route path="ministry" element={<SuperMinistry />} />
        <Route path="campaigns" element={<Campaigns />} />
        <Route path="audit" element={<SuperAudit />} />
      </Route>

      <Route path="/dashboard" element={
        <ProtectedRoute allow={["hospital_admin", "doctor", "nurse", "caregiver", "secretary"]}>
          <DashboardLayout />
        </ProtectedRoute>
      }>
        <Route index element={<DashboardHome />} />
        <Route path="my-day" element={<MyDay />} />
        <Route path="patients" element={<Patients />} />
        <Route path="hospitalizations" element={<Hospitalizations />} />
        <Route path="deaths" element={<Deaths />} />
        <Route path="appointments" element={<Appointments />} />
        <Route path="advice" element={<Advice />} />
        <Route path="sms" element={<SMSLog />} />
        <Route path="team" element={<Team />} />
        <Route path="audit" element={<Audit />} />
        <Route path="subscribe" element={<Subscribe />} />
        <Route path="services/urgences" element={<Urgences />} />
        <Route path="services/maternite" element={<Maternite />} />
        <Route path="services/pediatrie" element={<Pediatrie />} />
        <Route path="services/pharmacie" element={<Pharmacie />} />
        <Route path="epidemic-alerts" element={<EpidemicAlerts />} />
        <Route path="dhis2" element={<Dhis2Export />} />
      </Route>

      <Route path="/ministry" element={
        <ProtectedRoute allow={["ministry"]}>
          <MinistryDashboard />
        </ProtectedRoute>
      } />

      <Route path="/ministry/provinces/:province" element={
        <ProtectedRoute allow={["ministry", "super_admin"]}>
          <ProvinceDetail />
        </ProtectedRoute>
      } />
      <Route path="/ministry/hospitals/:id" element={
        <ProtectedRoute allow={["ministry", "super_admin"]}>
          <HospitalStats />
        </ProtectedRoute>
      } />

      <Route path="/ministry/report" element={
        <ProtectedRoute allow={["ministry", "super_admin"]}>
          <MinistryReport />
        </ProtectedRoute>
      } />

      <Route path="/formation" element={<TrainingGuide />} />
      <Route path="/formation/quiz/:role" element={<FormationQuiz />} />
      <Route path="/formation/certificat/:cid" element={<FormationCertificate />} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <AppProvider>
        <BrowserRouter>
          <GoogleAnalytics />
          <AppRoutes />
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </AppProvider>
    </div>
  );
}

export default App;
